import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Search } from "lucide-react";
import { InviteModal, SuccessModal } from "../../components/modal";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const DEPARTMENTS_CACHE_KEY = "cehr_company_departments_v1";

const Department = () => {
  const cachedData = useMemo(
    () => readSessionCache(DEPARTMENTS_CACHE_KEY, { departments: [] }),
    [],
  );
  const [opendepartmentModal, setOpenDepartmentModal] = useState(false);
  const [opensucesssModal, setOpensuccessModal] = useState(false);
  const [departments, setDepartments] = useState(cachedData?.departments || []);
  const [departmentName, setDepartmentName] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(!cachedData?.departments?.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const openModal = () => setOpenDepartmentModal(true);
  const apiBaseUrl = getApiBaseUrl();

  const loadDepartments = async (silent = false) => {
    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    try {
      if (!silent && departments.length === 0) {
        setIsLoading(true);
      }
      setErrorMessage("");

      const response = await axios.get(
        `${apiBaseUrl}/organizations/departments`,
      );
      const nextDepartments = response?.data?.departments || [];
      setDepartments(nextDepartments);
      writeSessionCache(DEPARTMENTS_CACHE_KEY, {
        departments: nextDepartments,
      });
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to load departments. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDepartments(true);
  }, []);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadDepartments(true);
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [departments.length]);

  const resetForm = () => {
    setDepartmentName("");
    setDescription("");
    setErrorMessage("");
  };

  const closeDepartmentModal = () => {
    setOpenDepartmentModal(false);
    resetForm();
  };

  const createHandleSuccess = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    if (!departmentName.trim() || !description.trim()) {
      setErrorMessage("Department name and description are required.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.post(`${apiBaseUrl}/organizations/departments`, {
        departmentName: departmentName.trim(),
        description: description.trim(),
      });

      closeDepartmentModal();
      setOpensuccessModal(true);
      await loadDepartments();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to create department. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  let navigate = useNavigate();

  const filteredDepartments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return departments;
    return departments.filter((department) =>
      [department.departmentName, department.description].some((value) =>
        `${value || ""}`.toLowerCase().includes(query),
      ),
    );
  }, [departments, searchTerm]);

  const viewDepartment = (department) => {
    sessionStorage.setItem("selectedDepartmentId", department.id);
    navigate("/company-settings/department-details", { state: { departmentId: department.id } });
  };
  return (
    <div className="h-full w-full px-4 py-5 sm:p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Departments" },
        ]}
        title="Departments"
        subtitle="Create and manage organization departments."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            onClick={openModal}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        }
      />

      <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div><h2 className="font-semibold text-[#071C50]">All departments</h2><p className="mt-0.5 text-xs text-slate-500">{departments.length} department{departments.length === 1 ? "" : "s"} in your organization</p></div>
          <label className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 sm:w-72"><Search className="h-4 w-4 shrink-0 text-slate-400" /><span className="sr-only">Search departments</span><input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search departments" className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400" /></label>
        </div>
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[680px] border-collapse text-left">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500"><tr><th scope="col" className="w-[30%] px-6 py-3.5">Department</th><th scope="col" className="px-6 py-3.5">Description</th><th scope="col" className="w-32 px-6 py-3.5 text-right">Action</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {!isLoading && filteredDepartments.length === 0 ? <tr><td colSpan={3} className="px-6 py-12 text-center text-sm text-slate-500">{searchTerm ? "No departments match your search." : "No departments found. Create one to get started."}</td></tr> : filteredDepartments.map((department) => (
                <tr key={department.id} className="group transition-colors hover:bg-blue-50/40">
                  <td className="px-6 py-5 align-top"><p className="font-semibold text-[#071C50]">{department.departmentName}</p></td>
                  <td className="px-6 py-5 text-sm leading-6 text-slate-600">{department.description || "No description provided."}</td>
                  <td className="px-6 py-5 text-right align-top"><button type="button" onClick={() => viewDepartment(department)} aria-label={`View ${department.departmentName}`} className="inline-flex items-center gap-2 rounded-lg border border-blue-100 px-3 py-2 text-sm font-semibold text-[#1234CD] transition hover:border-[#1234CD] hover:bg-blue-50"><Eye className="h-4 w-4" /><span>View</span></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <InviteModal
        isOpen={opendepartmentModal}
        onClose={closeDepartmentModal}
      >
        <div className="my-8">
          <div>
            <p className=" text-2xl font-semibold text-black font-Outfit">
              Create Department
            </p>
          </div>
          <form
            className="my-12"
            onSubmit={createHandleSuccess}
          >
            {errorMessage && (
              <p
                className="mb-4 text-sm text-red-600"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="invite-email"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Department Name
              </label>
              <input
                type="text"
                id="text"
                placeholder="Title"
                value={departmentName}
                onChange={(ev) => setDepartmentName(ev.target.value)}
                className="w-full p-2 text-center mb-2  rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="invite-email"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Description
              </label>

              <textarea
                id="text"
                placeholder="Description"
                value={description}
                onChange={(ev) => setDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-36 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className=" flex justify-center mt-20 items-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] font-satoshi  leading-[21.6px] w-40 rounded-xl text-white py-[16px] bg-[#1234CD]  h-[40px]text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>{isSubmitting ? "Creating..." : "Create"}</span>
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={opensucesssModal}
        onClose={() => setOpensuccessModal(false)}
      />

      {isLoading && departments.length === 0 ? (
        <FetchLoadingState message="Loading departments..." />
      ) : null}
    </div>
  );
};

export default Department;
